/* eslint-disable react/jsx-props-no-spreading */
import { RoutesProvider, ModalsProvider, FontsProvider } from '@micromag/core/contexts';
import { DataProvider } from '@micromag/data';
import { ModalProvider } from '@panneau/core/contexts';
import { UppyProvider } from '@panneau/uppy';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import * as AppPropTypes from '../lib/PropTypes';
import Api from '../lib/api/Api';
import ViewerApi from '../lib/api/ViewerApi';

import { AppProvider } from '../contexts/AppContext';
import { AuthProvider } from '../contexts/AuthContext';
import { DataCacheProvider } from '../contexts/DataCacheContext';
import { MediaPipelinesProvider } from '../contexts/MediaPipelinesContext';
import { OrganisationProvider } from '../contexts/OrganisationContext';
import { PublicationsProvider } from '../contexts/PublicationsContext';
import { SiteProvider } from '../contexts/SiteContext';
import { StoryLinksProvider } from '../contexts/StoryLinksContext';
// import MainLayout from './layouts/Main';
import RoutesPublic from './routes/Public';
import RoutesStudio from './routes/Studio';
import RoutesViewer from './routes/Viewer';

const propTypes = {
    routes: AppPropTypes.routes.isRequired,
    publications: AppPropTypes.publicationServices.isRequired,
    page: AppPropTypes.page.isRequired,
    user: AppPropTypes.user,
    organisation: AppPropTypes.organisation,
    storyLink: AppPropTypes.storyLink,
    tus: PropTypes.shape({
        endpoint: PropTypes.string,
    }),
    xhr: PropTypes.shape({
        endpoint: PropTypes.string,
    }),
    domain: PropTypes.string.isRequired,
    mediaPipelines: AppPropTypes.mediaPipelines,
    viewerGoogleAnalyticsId: PropTypes.string,
};

const defaultProps = {
    user: null,
    organisation: null,
    storyLink: null,
    tus: {
        endpoint: `${window.location.protocol}//${window.location.host}/tus`,
        chunkSize: 1000000,
    },
    xhr: {
        endpoint: `${window.location.protocol}//${window.location.host}/api/xhr/upload`,
    },
    mediaPipelines: null,
    viewerGoogleAnalyticsId: null,
};

const App = ({
    routes,
    user,
    organisation,
    publications,
    storyLink,
    tus,
    xhr,
    domain,
    page,
    mediaPipelines,
    viewerGoogleAnalyticsId,
}) => {
    const { fonts = [] } = organisation || {};
    const customFonts = useMemo(
        () =>
            fonts.map((font) => ({
                ...font,
                type: 'custom',
            })),
        [fonts],
    );

    const api = useMemo(
        () =>
            domain === 'viewer'
                ? new ViewerApi({
                      routes,
                  })
                : new Api({
                      routes,
                  }),
        [routes, domain],
    );

    if (domain === 'default') {
        return (
            <FontsProvider customFonts={customFonts}>
                <RoutesProvider routes={routes}>
                    <DataProvider api={api}>
                        <SiteProvider page={page} viewerGoogleAnalyticsId={viewerGoogleAnalyticsId}>
                            <RoutesPublic domain={domain} />
                        </SiteProvider>
                    </DataProvider>
                </RoutesProvider>
            </FontsProvider>
        );
    }

    if (domain === 'viewer') {
        return (
            <FontsProvider customFonts={customFonts}>
                <DataProvider api={api}>
                    <StoryLinksProvider links={storyLink !== null ? [storyLink] : null}>
                        <RoutesProvider routes={routes}>
                            <SiteProvider
                                page={page}
                                viewerGoogleAnalyticsId={viewerGoogleAnalyticsId}
                            >
                                <RoutesViewer domain={domain} />
                            </SiteProvider>
                        </RoutesProvider>
                    </StoryLinksProvider>
                </DataProvider>
            </FontsProvider>
        );
    }

    return (
        <UppyProvider transport="tus" tus={tus} xhr={xhr}>
            <FontsProvider customFonts={customFonts}>
                <RoutesProvider routes={routes}>
                    <ModalsProvider>
                        <ModalProvider>
                            <DataProvider api={api}>
                                <AuthProvider user={user}>
                                    <OrganisationProvider organisation={organisation}>
                                        <DataCacheProvider>
                                            <PublicationsProvider services={publications}>
                                                <MediaPipelinesProvider
                                                    mediaPipelines={mediaPipelines}
                                                >
                                                    <AppProvider>
                                                        <SiteProvider
                                                            page={page}
                                                            domain={domain}
                                                            viewerGoogleAnalyticsId={
                                                                viewerGoogleAnalyticsId
                                                            }
                                                        >
                                                            <RoutesStudio domain={domain} />
                                                        </SiteProvider>
                                                    </AppProvider>
                                                </MediaPipelinesProvider>
                                            </PublicationsProvider>
                                        </DataCacheProvider>
                                    </OrganisationProvider>
                                </AuthProvider>
                            </DataProvider>
                        </ModalProvider>
                    </ModalsProvider>
                </RoutesProvider>
            </FontsProvider>
        </UppyProvider>
    );
};

App.propTypes = propTypes;
App.defaultProps = defaultProps;

export default App;
